import {
  Paper,
  Popper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import { useDrawsStyles } from "./Draws.styles";
import { sanitizeCurrency } from "../../utils/formatChecks";
import { getDrawDateCreated } from "../AssetManagement.utils";

const DRAW_HISTORY_CLOSE_DELAY_MS = 250;

const DrawHistoryPopupArrow = styled("div")({
  position: "absolute",
  width: 15,
  height: 15,
  left: -8,
  zIndex: 1,
  pointerEvents: "none",
  "&::before": {
    content: '""',
    margin: "auto",
    display: "block",
    width: 0,
    height: 0,
    borderStyle: "solid",
    borderColor: "transparent #ffffff transparent transparent",
    borderWidth: "5px 5px 5px 0"
  },
  "&::after": {
    content: '""',
    width: 0,
    height: 0,
    margin: "auto",
    display: "block",
    borderStyle: "solid",
    borderColor: "transparent #dde0e5 transparent transparent",
    borderWidth: "8px 8px 8px 0",
    marginTop: "-13px",
    zIndex: -1,
    position: "relative"
  }
});

const DrawHistoryTable = ({ historyList }: { historyList: any[] }) => {
  const classes = useDrawsStyles();

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      className={classes.drawHistoryTableContainer}
    >
      <Table style={{ width: 660 }}>
        <TableHead>
          <TableRow>
            <TableCell align="left" className={classes.tooltipTableHead}>
              Draw Request ID
            </TableCell>
            <TableCell align="left" className={classes.tooltipTableHead}>
              Date Requested
            </TableCell>
            <TableCell align="left" className={classes.tooltipTableHead}>
              Approved Draw Amount ($)
            </TableCell>
            <TableCell align="left" className={classes.tooltipTableHead}>
              Draw Paid Date
            </TableCell>
            <TableCell align="left" className={classes.tooltipTableHead}>
              Net Funds to Borrower ($)
            </TableCell>
            <TableCell align="left" className={classes.tooltipTableHead}>
              Draw Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {historyList.map((drawitem) => (
            <TableRow key={drawitem.id || drawitem.drawId}>
              <TableCell
                component="th"
                scope="drawitem"
                className={classes.tooltipTableCell}
              >
                {drawitem.drawId}
              </TableCell>
              <TableCell align="left" className={classes.tooltipTableCell}>
                {getDrawDateCreated(drawitem.drawRequestedDate)}
              </TableCell>
              <TableCell align="left" className={classes.tooltipTableCell}>
                {sanitizeCurrency(drawitem.approvedDrawAmount?.toString())}
              </TableCell>
              <TableCell align="left" className={classes.tooltipTableCell}>
                {getDrawDateCreated(drawitem.drawPaidDate)}
              </TableCell>
              <TableCell align="left" className={classes.tooltipTableCell}>
                {sanitizeCurrency(drawitem.netFundsToBorrower?.toString())}
              </TableCell>
              <TableCell align="left" className={classes.tooltipTableCell}>
                {drawitem.drawStatus}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const DrawHistoryComponent = ({
  historyList
}: {
  historyList: any[];
}) => {
  const classes = useDrawsStyles();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const hideHistoryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openHistory = () => {
    if (hideHistoryTimerRef.current) {
      clearTimeout(hideHistoryTimerRef.current);
      hideHistoryTimerRef.current = null;
    }
    setShowHistory(true);
  };

  const scheduleCloseHistory = () => {
    if (hideHistoryTimerRef.current) {
      clearTimeout(hideHistoryTimerRef.current);
    }
    hideHistoryTimerRef.current = setTimeout(() => {
      setShowHistory(false);
      hideHistoryTimerRef.current = null;
    }, DRAW_HISTORY_CLOSE_DELAY_MS);
  };

  const toggleHistory = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (hideHistoryTimerRef.current) {
      clearTimeout(hideHistoryTimerRef.current);
      hideHistoryTimerRef.current = null;
    }
    setShowHistory((prev) => !prev);
  };

  useEffect(() => {
    return () => {
      if (hideHistoryTimerRef.current) {
        clearTimeout(hideHistoryTimerRef.current);
      }
    };
  }, []);

  if (!historyList?.length) {
    return null;
  }

  return (
    <>
      <span
        ref={anchorRef}
        data-testid="draw-history-hover-area"
        role="none"
        style={{
          display: "inline-flex",
          alignItems: "center"
        }}
        onMouseEnter={openHistory}
        onMouseLeave={scheduleCloseHistory}
      >
        <div
          className={classes.editedLabel}
          style={{ backgroundColor: "#5e72e4" }}
          data-testid="draw-history"
        >
          <div className={classes.tagTitle}>DRAW HISTORY</div>
          <HistoryIcon
            fontSize="large"
            style={{ color: "#fff", cursor: "pointer" }}
            onClick={toggleHistory}
            data-testid="draw-history-icon"
          />
        </div>
      </span>
      <Popper
        open={showHistory}
        anchorEl={anchorRef.current}
        placement="right-end"
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8]
            }
          },
          {
            name: "preventOverflow",
            enabled: true
          },
          {
            name: "flip",
            enabled: true
          },
          {
            name: "arrow",
            enabled: true,
            options: {
              element: arrowRef
            }
          }
        ]}
      >
        <div
          data-testid="draw-history-popup"
          className={classes.drawHistoryPopupBridge}
          role="none"
          onMouseEnter={openHistory}
          onMouseLeave={scheduleCloseHistory}
        >
          <DrawHistoryPopupArrow
            ref={setArrowRef}
            data-testid="draw-history-popup-arrow"
          />
          <div className={classes.drawHistoryPopup}>
            <DrawHistoryTable historyList={historyList} />
          </div>
        </div>
      </Popper>
    </>
  );
};
